import { useCallback, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ThinFooter from "../Footer/ThinFooter";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import { HeadingWrapper } from "../Atomics/SectionHeader/SectionHeader";
import BasicTable from "../Atomics/Table";
import { commentsFetcher } from "../../api/requests";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Modal from "../Atomics/Modal";
import { Button } from "react-bootstrap";
import { useToaster } from "../../context/toaster";

const CommentManagement = () => {
  const [commentsData, setCommentsData] = useState([]);
  const [originalCommentsData, setOriginalCommentsData] = useState([]);
  const [originalCommentStateById, setOriginalCommentStateById] = useState({});
  const [loading, setLoading] = useState(false);
  const param = useParams();
  const videoId = Number(param.id);
  const [showDelModal, setShowDelModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [commentState, setCommentState] = useState(null);
  const { addMessage } = useToaster();

  const showToast = (type, text) => {
    addMessage({
      id: Math.random().toString(36).substr(2, 9),
      type: type,
      text: text,
    });
  };

  const getCommentsData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commentsFetcher.getAll(videoId);

      if (res.count > 0) {
        const items = res.results || [];

        // Build original state map by id (including sub_comments)
        const originalStateMap = {};
        items.forEach((c) => {
          originalStateMap[c.id] = c.confirm_to_comment;
          if (Array.isArray(c.sub_comments)) {
            c.sub_comments.forEach((sub) => {
              originalStateMap[sub.id] = sub.confirm_to_comment;
            });
          }
        });
        setOriginalCommentStateById(originalStateMap);

        // API already returns properly nested structure with sub_comments
        // Only filter to show parent-level comments (those with uper_comment: null)
        const parentComments = items.filter((c) => !c?.uper_comment);

        setCommentsData(parentComments);
        setOriginalCommentsData(parentComments);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  const handleSaveApprove = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commentsFetcher.put(selectedComment, {
        confirm_to_comment: commentState,
        video_post: videoId,
      });
      setShowSaveModal(false);

      setCommentsData((prev) =>
        prev.map((item) => {
          if (item.id === selectedComment) {
            return { ...item, hasChanged: false };
          }

          const key = item.replies
            ? "replies"
            : item.children
            ? "children"
            : item.sub_comments
            ? "sub_comments"
            : item.subComments
            ? "subComments"
            : null;

          if (!key || !Array.isArray(item[key])) return item;

          const updatedChildren = item[key].map((child) =>
            child.id === selectedComment ? { ...child, hasChanged: false } : child
          );

          return { ...item, [key]: updatedChildren };
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedComment, commentState]);

  const handleCommentStateChange = (index, newState) => {
    setCommentsData((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, confirm_to_comment: newState, hasChanged: true }
          : item
      )
    );
  };

  const handleChildCommentStateChange = (parentIndex, childIndex, newState) => {
    setCommentsData((prev) => {
      const updated = prev.map((item, i) => {
        if (i !== parentIndex) return item;

        const key = item.replies
          ? "replies"
          : item.children
          ? "children"
          : item.sub_comments
          ? "sub_comments"
          : item.subComments
          ? "subComments"
          : null;


        if (!key || !Array.isArray(item[key])) return item;

        const updatedChildren = item[key].map((child, ci) => {
          if (ci === childIndex) {
            return {
              ...child,
              confirm_to_comment: newState,
              hasChanged: true,
            };
          }
          return child;
        });

        return { ...item, [key]: updatedChildren };
      });
      return updated;
    });
  };

  const handleSaveCancel = (index) => {
    setCommentsData((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const originalValue = originalCommentStateById[item.id];
          return {
            ...item,
            confirm_to_comment: originalValue,
            hasChanged: false,
          };
        }
        return item;
      })
    );
  };

  const handleChildSaveCancel = (parentIndex, childIndex) => {
    setCommentsData((prev) =>
      prev.map((item, i) => {
        if (i !== parentIndex) return item;

        const key = item.replies
          ? "replies"
          : item.children
          ? "children"
          : item.sub_comments
          ? "sub_comments"
          : item.subComments
          ? "subComments"
          : null;

        if (!key || !Array.isArray(item[key])) return item;

        const updatedChildren = (item[key] || []).map((child, ci) =>
          ci === childIndex
            ? {
                ...child,
                confirm_to_comment: originalCommentStateById[child.id],
                hasChanged: false,
              }
            : child
        );

        return { ...item, [key]: updatedChildren };
      })
    );
  };

  const handleDelApprove = async () => {
    try {
      const res = await commentsFetcher.delete(selectedComment);
      setShowDelModal(false);

      if (res.message) showToast("success", "نظر با موفقیت حذف شد.");
      
      // Refetch comments after successful deletion
      await getCommentsData();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCommentsData();
  }, [videoId, getCommentsData]);

  return (
    <ContentWrapper>
      <Container
        fluid
        className="upload-details text-right"
        style={{
          paddingLeft: "30px",
          paddingRight: "30px",
          minHeight: "calc(100vh - 123px)",
        }}
      >
        <div
          style={{
            paddingTop: "30px",
            paddingLeft: "30px",
            paddingRight: "30px",
          }}
        >
          <Row>
            <Col>
              <div className="float-right">
                <HeadingWrapper heading="مدیریت نظرات" />
              </div>
            </Col>
          </Row>

          <Row className="px-3">
            <BasicTable
              cols={["تایید", "کاربر", "نظر", "تاریخ"]}
              rows={commentsData}
              name="comments"
              rowsLoading={loading}
              setShowDelModal={setShowDelModal}
              setSelectedComment={setSelectedComment}
              setCommentState={setCommentState}
              onCommentStateChange={handleCommentStateChange}
              onChildCommentStateChange={handleChildCommentStateChange}
              handleSaveCancel={handleSaveCancel}
              handleChildSaveCancel={handleChildSaveCancel}
              setShowSaveModal={setShowSaveModal}
            />
          </Row>
        </div>
      </Container>

      <Modal isOpen={showDelModal} onClose={() => setShowDelModal(false)}>
        <p style={{ paddingTop: "15px", fontSize: "18px", width: "100%" }}>
          از حذف این آیتم مطمئن هستید؟
        </p>
        <div style={{ display: "flex", gap: "5px", width: "100%" }}>
          <Button
            style={{
              fontSize: "15px",
              padding: "2px 8px",
              height: "fit-content",
            }}
            onClick={() => handleDelApprove()}
          >
            بله
          </Button>
          <Button
            style={{
              fontSize: "15px",
              padding: "2px 8px",
              height: "fit-content",
            }}
            variant="danger"
            onClick={() => setShowDelModal(false)}
          >
            خیر
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showSaveModal} onClose={() => setShowSaveModal(false)}>
        <p style={{ paddingTop: "15px", fontSize: "18px", width: "100%" }}>
          از این تغییر مطمئن هستید؟
        </p>
        <div style={{ display: "flex", gap: "5px", width: "100%" }}>
          <Button
            style={{
              fontSize: "15px",
              padding: "2px 8px",
              height: "fit-content",
            }}
            onClick={() => handleSaveApprove()}
          >
            بله
          </Button>
          <Button
            style={{
              fontSize: "15px",
              padding: "2px 8px",
              height: "fit-content",
            }}
            variant="danger"
            onClick={() => setShowSaveModal(false)}
          >
            خیر
          </Button>
        </div>
      </Modal>

      <ThinFooter />
    </ContentWrapper>
  );
};

export default CommentManagement;
